import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  Message,
  MessageStatus,
  PatientMessagesRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientMessagesStore — sent + draft SMS messages for the current patient.
 *
 * Auto-loads on patient change, owns the MessageMessageSent push
 * subscription, and exposes createDraft() which seeds a new message
 * DTO from the patient's first cell number, persists it via the repo,
 * and returns the new id for the caller to register with DraftsService.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientMessagesStore {
  private patientStore = inject(PatientStore);
  private repo = inject(PatientMessagesRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Message[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.MessageMessageSent;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Create a new SMS draft seeded from the current patient's first cell
   * contact (type === 2). Returns the new message id; caller registers
   * via DraftsService and reloads the list afterwards.
   */
  createDraft(): Observable<number> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientMessagesStore.createDraft() called with no current patient');
    }
    const cellNumber = patient.contactNumbers?.find(c => c.type === 2)?.number ?? '';
    const toArray = cellNumber ? [cellNumber] : [];
    const message = {
      patientId: patient.id,
      to: toArray,
      content: '',
      status: MessageStatus.DRAFT
    } as Message;
    return this.repo.create(message).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadAll(patientId).subscribe({
      next: messages => {
        this._items.set(messages);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient messages');
        
      }
    });
  }
}
